import { t } from "@lingui/core/macro";
import {
  RouteProp,
  useIsFocused,
  useNavigation,
  NavigationProp,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { addDays, format } from "date-fns";
import { useAtom, useAtomValue } from "jotai";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import {
  PreloadedQuery,
  loadQuery as loadRelayQuery,
  useFragment,
  usePreloadedQuery,
  useRelayEnvironment,
} from "react-relay";

import { AgendaDateSelectorControlFragmentString } from "@/components/AgendaFilters/components/DateSelectorControl/AgendaDateSelectorControl";
import type { AgendaDateSelectorControlFragment_facility$key } from "@/components/AgendaFilters/components/DateSelectorControl/__generated__/AgendaDateSelectorControlFragment_facility.graphql";
import { BookingWizard } from "@/features/BookingWizard/BookingWizard";
import { RequestBookingWizard } from "@/features/BookingWizard/RequestBookingWizard";
import { useFlags } from "@/utils/launchDarkly";
import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import {
  COLUMN_MIN_WIDTH_PX,
  TIMEBAR_WIDTH_PX,
} from "@whoosh/design-system/src/apps/PatronApp/screens/multiSlotBooking";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import {
  MobileHeader,
  getClampedScrollOffsetY,
} from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { usePatronTopbarMobileHeaderNav } from "../../../../components/PageWrapper/hooks/usePatronTopbarMobileHeaderNav";
import { FiltersModal } from "../../../../features/Agenda/components/FiltersModal";
import { WaitlistModal } from "../../../../features/Agenda/components/WaitlistModal";
import { currentProfileAtom } from "../../../../features/Club/atoms";
import { TeeSheetAgendaHeader } from "../../../../features/TeeSheet/components/TeeSheetAgendaHeader/TeeSheetAgendaHeader";
import { getFacilityMemberPermissions } from "../../../../hooks/getFacilityMemberPermissions";
import { useGetClubStartOfWeekNumberPatron } from "../../../../hooks/useGetClubStartOfWeekNumberPatron";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../../../navigators/HomeTabsNavigator/types";
import { MultiSlotBookingScreenRendererQuery } from "./__generated__/MultiSlotBookingScreenRendererQuery.graphql";
import { multiSlotRequestBookingInfoAtom } from "./atoms";
import { selectedCoursesByFacility } from "./atoms/selectedCoursesByFacility";
import {
  MultiSlotAgenda,
  MultiSlotAgendaEventsQueryString,
  MultiSlotAgendaQueryString,
} from "./components/MultiSlotAgenda/MultiSlotAgenda";
import { MultiSlotAgendaEventsQuery } from "./components/MultiSlotAgenda/__generated__/MultiSlotAgendaEventsQuery.graphql";
import { MultiSlotAgendaQuery } from "./components/MultiSlotAgenda/__generated__/MultiSlotAgendaQuery.graphql";
import { MultiSlotSidebar } from "./components/MultiSlotCourseFilters/MultiSlotSidebar";
import { getCurrentDateStringOrDefaultToToday } from "./utils";

export const MIN_SLOT_DURATION_MINUTES = 15;
export const DEFAULT_SLOT_DURATION_MINUTES = 30;
export const MULTI_SLOT_AGENDA_COURSE_BATCH_SIZE = 5;

type MultiSlotAgendaQueryRefs = {
  eventsQueryRef: PreloadedQuery<MultiSlotAgendaEventsQuery>;
  courseQueryRefs: readonly PreloadedQuery<MultiSlotAgendaQuery>[];
};

export const getMultiSlotAgendaCourseIdBatches = (
  courseIds: readonly string[]
) => {
  return Array.from(
    {
      length: Math.ceil(courseIds.length / MULTI_SLOT_AGENDA_COURSE_BATCH_SIZE),
    },
    (_, batchIndex) =>
      courseIds.slice(
        batchIndex * MULTI_SLOT_AGENDA_COURSE_BATCH_SIZE,
        (batchIndex + 1) * MULTI_SLOT_AGENDA_COURSE_BATCH_SIZE
      )
  );
};

const disposeAgendaQueryRefs = (queryRefs: MultiSlotAgendaQueryRefs | null) => {
  queryRefs?.eventsQueryRef.dispose();
  queryRefs?.courseQueryRefs.forEach((queryRef) => queryRef.dispose());
};

export const MultiSlotAgendaSkeleton = () => {
  const placeholderColumns = Array.from({ length: 5 }, (_, index) => index);
  const placeholderRows = Array.from({ length: 8 }, (_, index) => index);

  return (
    <Box testID="multi-slot-agenda-skeleton" flex={1} bg="surface-primary">
      <HStack height={50} borderBottomWidth={1} borderColor="surface-dividers">
        <Box width={TIMEBAR_WIDTH_PX} bg="surface-primary" />
        {placeholderColumns.map((column) => (
          <Box
            key={column}
            minWidth={COLUMN_MIN_WIDTH_PX}
            flex={1}
            px={3}
            justifyContent="center"
            borderLeftWidth={1}
            borderColor="surface-dividers"
          >
            <Box
              height={3}
              width="60%"
              borderRadius="6"
              bg="surface-secondary"
            />
          </Box>
        ))}
      </HStack>
      <HStack flex={1}>
        <VStack width={TIMEBAR_WIDTH_PX} bg="surface-primary">
          {placeholderRows.map((row) => (
            <Box
              key={row}
              height={72}
              alignItems="center"
              borderTopWidth={1}
              borderColor="surface-dividers"
              pt={2}
            >
              <Box
                height={2}
                width="50%"
                borderRadius="6"
                bg="surface-secondary"
              />
            </Box>
          ))}
        </VStack>
        <HStack flex={1} bg="surface-primary">
          {placeholderColumns.map((column) => (
            <VStack
              key={column}
              minWidth={COLUMN_MIN_WIDTH_PX}
              flex={1}
              borderLeftWidth={1}
              borderColor="surface-dividers"
            >
              {placeholderRows.map((row) => (
                <Box
                  key={row}
                  height={72}
                  borderTopWidth={1}
                  borderColor="surface-dividers"
                  p={3}
                >
                  <Box
                    height={10}
                    width="100%"
                    borderRadius="4"
                    bg="surface-secondary"
                  />
                </Box>
              ))}
            </VStack>
          ))}
        </HStack>
      </HStack>
    </Box>
  );
};

const getRacquetBoldHeaderText = (facilityDisplayName?: string | null) => {
  if (!facilityDisplayName) {
    return t`Court`;
  }
  // Capitalize the first letter, lowercase the rest
  return (
    facilityDisplayName.charAt(0).toUpperCase() +
    facilityDisplayName.slice(1).toLowerCase()
  );
};

type MultiSlotBookingScreenRendererProps = {
  queryRef: PreloadedQuery<MultiSlotBookingScreenRendererQuery>;
  currentSlotId?: string;
  handleMultiSlotRowPress?: (slotId: string) => void;
};

export const MultiSlotBookingScreenRenderer = ({
  queryRef,
  currentSlotId,
  handleMultiSlotRowPress,
}: MultiSlotBookingScreenRendererProps) => {
  const relayEnvironment = useRelayEnvironment();
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const homeNavigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const currentProfile = useAtomValue(currentProfileAtom);

  const [filtersModalVisible, setFiltersModalVisible] = useState(false);
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);

  const data = usePreloadedQuery<MultiSlotBookingScreenRendererQuery>(
    MultiSlotBookingScreenRendererQueryString,
    queryRef
  );
  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();
  const date = getCurrentDateStringOrDefaultToToday(route?.params?.date);

  const facilitySlug = route?.params?.facilitySlug;

  const [agendaQueryRefs, setAgendaQueryRefs] =
    useState<MultiSlotAgendaQueryRefs | null>(null);

  const [requestBookingInfo, setRequestBookingInfo] = useAtom(
    multiSlotRequestBookingInfoAtom
  );
  const handleRequestBookingFlowStart = useCallback(
    (queueSlotId: string, requestedTime: string | null) => {
      setRequestBookingInfo({
        queueSlotId,
        requestedTime,
      });
    },
    [setRequestBookingInfo]
  );

  const club = data.session?.currentClientProfile?.member?.club;

  const matchingFacility = club?.facilities?.edges?.find((el) => {
    return !!facilitySlug && el?.node?.slug === facilitySlug;
  });
  const matchingFacilityId = matchingFacility?.node?.id;
  const matchingFacilityDisplayName =
    matchingFacility?.node?.displayNameOrFacilityName;

  const facilityForDate =
    useFragment<AgendaDateSelectorControlFragment_facility$key>(
      AgendaDateSelectorControlFragmentString,
      matchingFacility?.node ?? null
    );

  const selectedCourses = useAtomValue(
    selectedCoursesByFacility(matchingFacilityId ?? "")
  );
  const selectedCourseIds = useMemo(
    () =>
      selectedCourses
        ? Object.keys(selectedCourses).filter(
            (courseId) => selectedCourses[courseId]
          )
        : [],
    [selectedCourses]
  );

  const courseNodes = useMemo(
    () =>
      matchingFacility?.node?.courses?.edges?.flatMap((edge) =>
        edge?.node ? [edge.node] : []
      ) ?? [],
    [matchingFacility?.node?.courses?.edges]
  );

  const agendaCourseIds = useMemo(() => {
    const selectedCourseIdSet = new Set(selectedCourseIds);

    if (!selectedCourseIdSet.size) {
      return courseNodes.map((course) => course.id);
    }

    return courseNodes
      .filter((course) => selectedCourseIdSet.has(course.id))
      .map((course) => course.id);
  }, [courseNodes, selectedCourseIds]);

  const agendaCourseIdBatches = useMemo(
    () => getMultiSlotAgendaCourseIdBatches(agendaCourseIds),
    [agendaCourseIds]
  );

  const courseFilterLabel = useMemo(() => {
    if (!courseNodes.length) {
      return null;
    }

    if (!selectedCourses) {
      return t`All Courts`;
    }

    const normalizedSelectedCourseIds = selectedCourseIds;
    const allCoursesSelected =
      normalizedSelectedCourseIds.length === courseNodes.length;

    if (allCoursesSelected) {
      return t`All Courts`;
    }

    if (!normalizedSelectedCourseIds.length) {
      return null;
    }

    const selectedCount = normalizedSelectedCourseIds.length;
    return selectedCount === 1 ? t`1 Court` : t`${selectedCount} Courts`;
  }, [courseNodes, selectedCourseIds, selectedCourses]);

  const { memberAccess } = getFacilityMemberPermissions({
    fragmentRef: matchingFacility?.node || null,
  });

  const { memAppWaitlist: memAppWaitlistFeatureFlag, requestConfigs } =
    useFlags();

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    facilityForDate?.club ?? null
  );
  const supportsPrivate = facilityForDate?.club?.supportsPrivate ?? false;
  const bookingWindowDays = supportsPrivate
    ? facilityForDate?.memberBookingWindowDays ?? 14
    : facilityForDate?.publicBookingWindowDays ?? 60;
  const todayString = format(new Date(), "yyyy-MM-dd");
  const bookingWindowEndDate = format(
    addDays(new Date(), bookingWindowDays),
    "yyyy-MM-dd"
  );
  const handleDateChange = useCallback(
    (newDate: string) => {
      if (newDate !== date) {
        navigation.setParams({ date: newDate });
      }
    },
    [date, navigation]
  );

  const isFocused = useIsFocused();

  useEffect(() => {
    if (
      matchingFacilityId &&
      !!date &&
      isFocused &&
      agendaCourseIdBatches.length
    ) {
      const nextQueryRefs = {
        eventsQueryRef: loadRelayQuery<MultiSlotAgendaEventsQuery>(
          relayEnvironment,
          MultiSlotAgendaEventsQueryString,
          {
            facilityId: matchingFacilityId,
            date,
            memberId: currentProfile?.memberId || "",
            requestConfigs: !!requestConfigs,
          },
          { fetchPolicy: "store-and-network" }
        ),
        courseQueryRefs: agendaCourseIdBatches.map((courseIds) =>
          loadRelayQuery<MultiSlotAgendaQuery>(
            relayEnvironment,
            MultiSlotAgendaQueryString,
            {
              facilityId: matchingFacilityId,
              date,
              courseIds,
              requestConfigs: !!requestConfigs,
            },
            { fetchPolicy: "store-and-network" }
          )
        ),
      };

      setAgendaQueryRefs((previousQueryRefs) => {
        disposeAgendaQueryRefs(previousQueryRefs);
        return nextQueryRefs;
      });

      return () => {
        disposeAgendaQueryRefs(nextQueryRefs);
      };
    }

    setAgendaQueryRefs((previousQueryRefs) => {
      disposeAgendaQueryRefs(previousQueryRefs);
      return null;
    });
  }, [
    agendaCourseIdBatches,
    currentProfile?.memberId,
    date,
    isFocused,
    matchingFacilityId,
    relayEnvironment,
    requestConfigs,
  ]);

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      homeNavigation.navigate("MainHome");
    }
  };

  const clubLogoImageUrl = club?.logoImage?.publicId
    ? getThumbnail(club.logoImage.publicId, 40, 40)?.toURL()
    : undefined;

  const { customNavigationBar, navigationBarVariant } =
    usePatronTopbarMobileHeaderNav({
      leftAction: "Back",
      onLeftActionClick: handleBackPress,
      title: matchingFacilityDisplayName ?? "",
      subTitle: "",
      clubLogoImageUrl,
    });
  const shouldUseMobileHeader = Boolean(customNavigationBar);
  const showWaitlist =
    shouldUseMobileHeader &&
    memAppWaitlistFeatureFlag &&
    memberAccess &&
    !["HIDDEN", "FACILITY_PERMISSIONS_DISABLED"].includes(memberAccess);

  const [bookingWizardSlotId, setBookingWizardSlotId] = useState<string | null>(
    null
  );
  const [bookingWizardDate, setBookingWizardDate] = useState<string | null>(
    null
  );
  const [bookingWizardStartTime, setBookingWizardStartTime] = useState<
    string | null
  >(null);

  const dismissBookingWizard = useCallback(() => {
    setBookingWizardSlotId(null);
    setBookingWizardDate(null);
    setBookingWizardStartTime(null);
  }, []);

  const scrollY = useSharedValue(0);
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = getClampedScrollOffsetY({
        offsetY: event.contentOffset.y,
        contentHeight: event.contentSize?.height,
        viewportHeight: event.layoutMeasurement?.height,
      });
    },
  });

  return (
    <>
      <AgendaLayout
        shouldCenter={false}
        header={
          club ? (
            shouldUseMobileHeader ? (
              <MobileHeader
                customNavigationBar={customNavigationBar}
                navigationBarVariant={navigationBarVariant}
                filtersBar={{
                  title: {
                    leadingText: t`Find`,
                    boldText: getRacquetBoldHeaderText(
                      matchingFacilityDisplayName
                    ),
                    trailingText: t`Times`,
                  },
                  values: [
                    {
                      value: courseFilterLabel,
                      placeholder: t`Courts`,
                    },
                  ],
                  onPress: () => setFiltersModalVisible(true),
                  rightAction: showWaitlist ? "Waitlist" : null,
                  onRightActionClick: showWaitlist
                    ? () => setIsWaitlistModalOpen(true)
                    : undefined,
                }}
                dateSwitcher={{
                  startDate: todayString,
                  endDate: bookingWindowEndDate,
                  selectedDate: date,
                  onChange: handleDateChange,
                  clubWeekStartsOn: clubWeekStartsOn,
                }}
                scrollY={scrollY}
              />
            ) : (
              <TeeSheetAgendaHeader
                isScrolled={false} // MultiSlot has a fixed header
                onFilterPress={() => setFiltersModalVisible(true)}
                onDatePress={() => {}}
                onWaitlistPress={() => setIsWaitlistModalOpen(true)}
                onBackPress={handleBackPress}
                title={t`Select Time`}
                dateString={date}
                variant="MULTI_SLOT"
                clubRef={club}
              />
            )
          ) : (
            <></>
          )
        }
        sidebarContent={
          <MultiSlotSidebar
            facilityRef={matchingFacility?.node ?? null}
            sessionRef={data.session ?? null}
            onJoinWaitlist={handleRequestBookingFlowStart}
          />
        }
        agenda={
          <Suspense fallback={<MultiSlotAgendaSkeleton />}>
            {agendaQueryRefs ? (
              <MultiSlotAgenda
                currentSlotId={currentSlotId}
                handleMultiSlotRowPress={handleMultiSlotRowPress}
                eventsQueryRef={agendaQueryRefs.eventsQueryRef}
                queryRefs={agendaQueryRefs.courseQueryRefs}
                onVerticalScroll={scrollHandler}
                setBookingWizardSlotId={(slotId) => {
                  setBookingWizardDate(date);
                  setBookingWizardSlotId(slotId);
                }}
                setBookingWizardStartTime={setBookingWizardStartTime}
              />
            ) : null}
          </Suspense>
        }
      />
      {shouldUseMobileHeader ? (
        <FiltersModal
          isOpen={filtersModalVisible}
          onClose={() => setFiltersModalVisible(false)}
          facilityRef={matchingFacility?.node ?? null}
          sessionRef={data.session ?? null}
          onJoinWaitlist={handleRequestBookingFlowStart}
        />
      ) : null}
      {shouldUseMobileHeader && matchingFacility?.node ? (
        <WaitlistModal
          fragmentRef={matchingFacility?.node}
          isOpen={isWaitlistModalOpen}
          onClose={() => setIsWaitlistModalOpen(false)}
          onJoinWaitlist={handleRequestBookingFlowStart}
          facilityId={matchingFacility?.node?.id ?? ""}
          date={date}
          courseIds={
            matchingFacility?.node?.courses?.edges?.map(
              (c) => c?.node?.id ?? ""
            ) ?? []
          }
        />
      ) : null}
      <BookingWizard
        isOpen={!!bookingWizardSlotId && !!bookingWizardStartTime}
        onDismiss={dismissBookingWizard}
        slotId={bookingWizardSlotId ?? null}
        initiallyLockedSlotIds={
          bookingWizardSlotId ? [bookingWizardSlotId] : []
        }
        membershipTypeId={
          data.session?.currentClientProfile?.member?.membershipType?.id ?? null
        }
        currentPatronId={data.session?.currentClientProfile?.member?.id ?? ""}
        currentDate={bookingWizardDate ?? date}
        clubSlug={data.session?.currentClientProfile?.member?.club?.slug ?? ""}
        slotType={"MULTI"}
      />
      <RequestBookingWizard
        isOpen={!!requestBookingInfo}
        onClose={() => {
          setRequestBookingInfo(null);
        }}
        slotId={requestBookingInfo?.queueSlotId ?? ""}
        membershipTypeId={
          data.session?.currentClientProfile?.member?.membershipType?.id ?? null
        }
        currentPatronId={data.session?.currentClientProfile?.member?.id ?? ""}
        clubSlug={data.session?.currentClientProfile?.member?.club?.slug ?? ""}
        queueSlotId={requestBookingInfo?.queueSlotId ?? ""}
        requestedTime={requestBookingInfo?.requestedTime ?? ""}
        initializationOverides={{
          durationMins: DEFAULT_SLOT_DURATION_MINUTES ?? undefined,
        }}
      />
    </>
  );
};
export const MultiSlotBookingScreenRendererQueryString = graphql`
  query MultiSlotBookingScreenRendererQuery($date: Date!) {
    session {
      ...MultiSlotSidebarSessionFragment_session
      ...FiltersModal_session
      currentClientProfile {
        member {
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
          membershipType {
            id
          }
          club {
            ...TeeSheetAgendaHeaderFragment_club
            ...FacilityLogoFragment_club
            id
            slug
            supportsPublic
            logoImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
            facilities {
              edges {
                node {
                  id
                  name
                  displayNameOrFacilityName
                  slug
                  ...AgendaDateSelectorControlFragment_facility
                  memberBookingWindowDays
                  ...getFacilityMemberPermissions_facility
                  ...TeeSheetBasedFiltersModalFragment_facility
                  ...WaitlistModalFragment_Facility
                  ...MultiSlotSidebarFragment_facility
                  ...FiltersModalFragment_facility
                  department {
                    type
                  }
                  club {
                    supportsPublic
                  }
                  courses {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
